import type { WidgetPreset } from "../types";
import previewTemplateHtml from "./preview.template.html?raw";
import { TimerWidget } from "./TimerWidget";

/**
 * Digital countdown timer — rendered as a React component (TimerWidget.tsx).
 *
 * generateTimerHtml is used ONLY for the static preview thumbnail in the
 * widget selector popover. The live canvas node is handled by TimerWidget,
 * which manages all state and interaction directly via Loro + React.
 *
 * State stored in Loro:
 *   totalSeconds    — current target duration
 *   isRunning       — true while counting down
 *   startedAt       — Date.now() when last started (null when paused/stopped)
 *   elapsedAtPause  — seconds elapsed up to the last pause
 */
export function generateTimerHtml(data: Record<string, unknown>): string {
  const totalSeconds = Math.max(
    10,
    Math.round(parseFloat(String(data.totalSeconds ?? 120)) || 120),
  );
  const isRunning = Boolean(data.isRunning);
  const startedAt = typeof data.startedAt === "number" ? data.startedAt : null;
  const elapsedAtPause = Math.max(
    0,
    parseFloat(String(data.elapsedAtPause ?? 0)) || 0,
  );

  const elapsed =
    isRunning && startedAt
      ? elapsedAtPause + (Date.now() - startedAt) / 1000
      : elapsedAtPause;

  const remaining = Math.max(0, totalSeconds - elapsed);
  const isFinished = remaining <= 0 && elapsedAtPause > 0;

  const mins = Math.floor(remaining / 60);
  const secs = Math.floor(remaining % 60);
  const minStr = String(mins).padStart(2, "0");
  const secStr = String(secs).padStart(2, "0");
  const progress = totalSeconds > 0 ? Math.max(0, remaining / totalSeconds) : 1;
  const barColor =
    progress > 0.5 ? "#22c55e" : progress > 0.2 ? "#f59e0b" : "#ef4444";
  const digitColor = isFinished ? "#fca5a5" : "#f9faf5";

  const DIGIT = `display:inline-block;width:96px;text-align:center;font-family:'Orbitron','Courier New',monospace;font-size:58px;font-weight:700;color:${digitColor};line-height:1;font-variant-numeric:tabular-nums`;

  const finishedBanner = isFinished
    ? '<div style="color:#fca5a5;font-size:11px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;margin-top:10px;opacity:.9">Time\'s up</div>'
    : "";

  return previewTemplateHtml
    .replaceAll("__TOP_BG__", isFinished ? "#450a0a" : "#111827")
    .replaceAll("__DIGIT_STYLE__", DIGIT)
    .replaceAll("__MIN_STR__", minStr)
    .replaceAll("__SEC_STR__", secStr)
    .replaceAll("__DIGIT_COLOR__", digitColor)
    .replaceAll("__FINISHED_BANNER__", finishedBanner)
    .replaceAll("__PROGRESS_PCT__", String(Math.round(progress * 100)))
    .replaceAll("__BAR_COLOR__", barColor);
}

export const TIMER_DIGITAL: WidgetPreset = {
  id: "timer-digital",
  label: "Meeting Timer ⏱",
  defaultWidth: 300,
  defaultHeight: 165,
  component: TimerWidget,
  resizable: { minWidth: 160, minHeight: 88 }, // ~50% of natural size
  previewHtml: generateTimerHtml({
    totalSeconds: 120,
    isRunning: false,
    startedAt: null,
    elapsedAtPause: 0,
  }),
  initialData: {
    totalSeconds: 120,
    isRunning: false,
    startedAt: null,
    elapsedAtPause: 0,
  },
};
